import {policy} from './policy.js';
import {resource,variables,localizer} from './fluent.js';
export {resource,variables} from './fluent.js';

import {english,languages,loaders} from './locale-catalog.js';
export {languages};
export const languageStorageKey='webreader.ui-language';
const listeners=new Set();

// Elm chooses messages and arguments; Fluent owns the shared text formatting.
export function createLocalizer(locale,text,fallback=english){return localizer(locale,text,fallback);}
const staticIds=resource(english).body.filter(message=>!message.id.startsWith('-')&&variables(message).length===0).map(message=>message.id);
export async function matchLanguage(preferences){
  const facts=preferences.flatMap(value=>{
    try{const canonical=Intl.getCanonicalLocales(value)[0],locale=new Intl.Locale(canonical).maximize();return [{canonical,language:locale.language,script:locale.script||''}];}
    catch{return [];}
  });
  return policy('language',{supported:languages.map(item=>item.code),preferences:facts});
}
let current='en',format=createLocalizer('en',english),generation=0,stateCache;
export const t=(id,args)=>format(id,args);
export const escapeUi=value=>String(value).replace(/[&<>"']/g,char=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
export const htmlMessage=(id,args)=>escapeUi(t(id,args));
export const onLanguageChange=callback=>{listeners.add(callback);return()=>listeners.delete(callback);};
export function localeState(){
  if(stateCache)return stateCache;
  const language=languages.find(item=>item.code===current);
  // Parameterized messages are formatted beside their owning state event.
  const messages=Object.fromEntries(staticIds.map(id=>[id,t(id)]));
  messages['locale-language']=current;
  messages['locale-direction']=language.direction;
  for(const [id,args]of Object.entries({
    'language-suggestion':{language:'{language}'},
    'ui-is-ready-to-read-offline':{value1:'{value1}'},
    'appearance-theme-name':{theme:'{theme}'},
    'audio-time-description':{position:'{position}',duration:'{duration}'},
    'book-library-kind':{kind:'{kind}'},
    'book-current-kind':{kind:'{kind}'},
  }))messages[id]=t(id,args);
  for(const rate of ['0.5','0.75','1','1.25','1.5','1.75','2','2.5','3'])messages['rate-'+rate]=t('audio-rate-value',{rate:Number(rate)});
  stateCache={language:current,direction:language.direction,languages,messages};
  return stateCache;
}
export async function setLanguage(language,{persist=true}={}){
  if(!languages.some(item=>item.code===language))throw Error('Unsupported interface language');
  const request=++generation;
  const catalog=await loaders[language]();
  const next=createLocalizer(language,catalog);
  if(request!==generation)return false;
  current=language;format=next;stateCache=undefined;
  if(persist)try{localStorage.setItem(languageStorageKey,language);}catch{}
  if(typeof document!=='undefined'){
    document.documentElement.style.setProperty('--ui-cjk-family',language==='zh-Hant'?'"Reading Noto Sans TC"':'"Reading Noto Sans SC"');
    if(language==='zh-CN'||language==='zh-Hant'){
      const face=language==='zh-Hant'?'noto-sans-tc':'noto-sans-sc';
      const href=new URL(`fonts/reading/${face}/index.css`,document.baseURI).href;
      if(![...document.querySelectorAll('link[rel="stylesheet"]')].some(link=>link.href===href)){
        const link=document.createElement('link');link.rel='stylesheet';link.href=href;document.head.append(link);
      }
    }
    document.documentElement.lang=language;
    document.documentElement.dir=languages.find(item=>item.code===language).direction;
  }
  for(const listener of listeners)listener(localeState());
  return true;
}
export async function initializeLanguage(){
  let saved;
  try{saved=localStorage.getItem(languageStorageKey);}catch{}
  const selected=await matchLanguage(saved?[saved]:['en']);
  try{await setLanguage(selected,{persist:false});}
  catch{await setLanguage('en',{persist:false});}
}
